import { ref, onUnmounted } from 'vue'

export interface TooltipContent {
  headline: string
  description: string
  image?: string
}

export interface TooltipPosition {
  x: number
  y: number
  placement?: 'top' | 'bottom'
  alignX?: 'center' | 'left' | 'right'
}

export function useTooltip() {
  const currentTooltip = ref<TooltipContent | null>(null)
  const tooltipPosition = ref<TooltipPosition | null>(null)
  const tooltipTimer = ref<number | null>(null)

  const showTooltip = (event: MouseEvent, content: TooltipContent | null) => {
    if (!content || typeof content !== 'object') return

    clearTimeout(tooltipTimer.value as number)

    // Get element position
    const rect = (event.target as HTMLElement).getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top

    // Estimate tooltip height (based on typical content)
    // A more accurate approach would require rendering first, but this is a good approximation
    const estimatedTooltipHeight = 200 // Approximate max height of tooltip
    const tooltipOffset = 8 // The translateY offset used in RichTooltip

    // Check if tooltip would overflow above viewport
    const wouldOverflowTop = y - estimatedTooltipHeight - tooltipOffset < 0

    // Check horizontal overflow (tooltip is w-80 = 320px)
    const tooltipWidth = 320
    const halfWidth = tooltipWidth / 2
    const viewportWidth = window.innerWidth
    let alignX: 'center' | 'left' | 'right' = 'center'
    if (x + halfWidth > viewportWidth) {
      alignX = 'right'
    } else if (x - halfWidth < 0) {
      alignX = 'left'
    }

    currentTooltip.value = content
    tooltipPosition.value = {
      x,
      y: wouldOverflowTop ? rect.bottom : y,
      placement: wouldOverflowTop ? 'bottom' : 'top',
      alignX
    }
  }

  const hideTooltip = () => {
    clearTimeout(tooltipTimer.value as number)
    tooltipTimer.value = window.setTimeout(() => {
      currentTooltip.value = null
      tooltipPosition.value = null
    }, 100)
  }

  const clearTooltip = () => {
    clearTimeout(tooltipTimer.value as number)
    currentTooltip.value = null
    tooltipPosition.value = null
  }

  // Cleanup on unmount
  onUnmounted(() => {
    clearTooltip()
  })

  return {
    currentTooltip,
    tooltipPosition,
    showTooltip,
    hideTooltip,
    clearTooltip
  }
}
